/**
 * ## Ours (Base UI)
 * Built on `@base-ui/react/dialog`. Animated, `side` + `variant` props.
 * * `SheetPortal` — portal
 * * `SheetBackdrop` / `SheetOverlay` — blurred backdrop
 * * `SheetViewport` — positioned container; `side?: 'right'|'left'|'top'|'bottom'`, `variant?: 'default'|'inset'`
 * * `SheetPopup` / `SheetContent` — collapses Portal + Backdrop + Viewport + Popup; `side`, `variant`, `showCloseButton?: boolean` (off by default)
 * * `SheetPanel` — scrollable body (`ScrollArea`); `scrollFade?: boolean`
 *
 * ## Shadcn UI
 * `SheetContent` has a built-in X close button (always on). No `SheetPanel`, no `variant`.
 * * `SheetOverlay` — semi-transparent overlay (no blur)
 * * `SheetContent` — collapses Portal + Overlay + Content + X close button; `side?: 'top'|'right'|'bottom'|'left'`
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */

'use client'

import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { XIcon } from './icon'
import { ScrollArea } from './scroll-area'
import { IconButton } from './icon-button'
import { cn } from '../lib/utils'

const Sheet = SheetPrimitive.Root
const SheetPortal = SheetPrimitive.Portal

function SheetTrigger(props: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose(props: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetBackdrop({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      className={cn(
        'fixed inset-0 z-50 backdrop-blur-md transition-[opacity,backdrop-filter] duration-300 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      data-slot="sheet-backdrop"
      {...props}
    />
  )
}

function SheetViewport({
  side,
  variant = 'default',
  ...props
}: SheetPrimitive.Viewport.Props & {
  side?: 'right' | 'left' | 'top' | 'bottom'
  variant?: 'default' | 'inset'
}) {
  return (
    <SheetPrimitive.Viewport
      className={cn(
        'fixed inset-0 z-50 grid',
        side === 'bottom' && 'grid grid-rows-[1fr_auto] pt-12',
        side === 'top' && 'grid grid-rows-[auto_1fr] pb-12',
        side === 'left' && 'flex justify-start',
        side === 'right' && 'flex justify-end',
        variant === 'inset' && 'sm:p-4',
      )}
      data-slot="sheet-viewport"
      {...props}
    />
  )
}

function SheetPopup({
  portalContainer,
  className,
  children,
  showCloseButton = false,
  side = 'right',
  variant = 'default',
  ...props
}: SheetPrimitive.Popup.Props & {
  portalContainer?: SheetPrimitive.Portal.Props['container']
  showCloseButton?: boolean
  side?: 'right' | 'left' | 'top' | 'bottom'
  variant?: 'default' | 'inset'
}) {
  return (
    <SheetPortal container={portalContainer}>
      <SheetBackdrop />
      <SheetViewport side={side} variant={variant}>
        <SheetPrimitive.Popup
          className={cn(
            'bg-surface-2 text-on-surface relative flex max-h-full min-h-0 w-full min-w-0 flex-col transition-[translate] duration-300 ease-out will-change-transform',
            side === 'bottom' &&
              'row-start-2 rounded-t-[28px] data-ending-style:translate-y-full data-starting-style:translate-y-full',
            side === 'top' &&
              'rounded-b-[28px] data-ending-style:-translate-y-full data-starting-style:-translate-y-full',
            side === 'left' &&
              'w-[calc(100%-(--spacing(12)))] max-w-md rounded-r-[28px] data-ending-style:-translate-x-full data-starting-style:-translate-x-full',
            side === 'right' &&
              'col-start-2 w-[calc(100%-(--spacing(12)))] max-w-md rounded-l-[28px] data-ending-style:translate-x-full data-starting-style:translate-x-full',
            variant === 'inset' &&
              'sm:rounded-[28px] sm:**:data-[slot=sheet-footer]:rounded-b-[calc(var(--radius-2xl)-1px)]',
            className,
          )}
          data-slot="sheet-popup"
          {...props}
        >
          {children}
          {showCloseButton && (
            <SheetPrimitive.Close
              aria-label="Close"
              className="absolute end-2 top-2"
              render={<IconButton />}
            >
              <XIcon />
              Close
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Popup>
      </SheetViewport>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pb-3 max-sm:pb-4',
        className,
      )}
      data-slot="sheet-header"
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 px-6 pt-4 pb-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-panel])]:pt-3 sm:flex-row sm:justify-end',
        className,
      )}
      data-slot="sheet-footer"
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      className={cn(
        'font-heading text-xl leading-none font-semibold',
        className,
      )}
      data-slot="sheet-title"
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      className={cn('text-on-surface-secondary text-sm', className)}
      data-slot="sheet-description"
      {...props}
    />
  )
}

function SheetPanel({
  className,
  scrollFade = true,
  ...props
}: React.ComponentProps<'div'> & { scrollFade?: boolean }) {
  return (
    <ScrollArea scrollFade={scrollFade}>
      <div
        className={cn(
          'p-6 in-[[data-slot=sheet-popup]:has([data-slot=sheet-footer]:not(.border-t))]:pb-1 in-[[data-slot=sheet-popup]:has([data-slot=sheet-header])]:pt-1',
          className,
        )}
        data-slot="sheet-panel"
        {...props}
      />
    </ScrollArea>
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetPortal,
  SheetClose,
  SheetBackdrop,
  SheetBackdrop as SheetOverlay,
  SheetPopup,
  SheetPopup as SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetPanel,
}
